import React, { useMemo } from 'react';

import { link } from '@aline/routes';
import { Paper, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { Spacer } from 'src/components/generic/Spacer';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useGetWorkflowsCount } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { AiReportsList, RecentDocuments, RemindersList, Templates } from './ResourceLists';

const useStyles = makeStyles()(theme => ({
  metrics: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
}));

export function Statistics() {
  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      <MetricsCards />
      <RecentDocuments />
      <Templates />
      <AiReportsList />
      <RemindersList />
    </div>
  );
}

export function MetricsCards() {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const { workflows } = useGetWorkflowsCount({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { data: reportData } = Apollo.useGetReportsCountQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { data: repositoryData } = Apollo.useGetDocumentMetricsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !currentUser?.myRepositoryId,
    variables: {
      reportId: currentUser?.myRepositoryId ?? '',
    },
  });
  const documentMetrics = useMemo(() => {
    return {
      documents: repositoryData?.report?.rows?.totalCount ?? 0,
    };
  }, [repositoryData?.report?.rows?.totalCount]);
  const reportMetrics = useMemo(() => {
    return { aiReports: reportData?.reports.length ?? 0 };
  }, [reportData?.reports.length]);
  const workflowMetrics = useMemo(() => {
    return {
      drafts: workflows.filter(
        w =>
          w.state === Apollo.WorkflowState.Negotiating ||
          w.state === Apollo.WorkflowState.ReadyForSignature
      ).length,
      signed: workflows.filter(
        w => w.state === Apollo.WorkflowState.Signed || w.state === Apollo.WorkflowState.Complete
      ).length,
      signing: workflows.filter(w => w.state === Apollo.WorkflowState.Signing).length,
      templates: workflows.filter(w => w.state === Apollo.WorkflowState.Template).length,
    };
  }, [workflows]);

  const getValueString = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 1,
      notation: 'compact',
    }).format(value);
  };

  return (
    <div className={classes.metrics}>
      <MetricsCard
        linkHref={link.WorkflowList()}
        linkText="Open workflows"
        title="Draft Workflows"
        value={getValueString(workflowMetrics.drafts)}
      />
      <MetricsCard
        linkHref={link.WorkflowList()}
        linkText="Open workflows"
        title="Signing Workflows"
        value={getValueString(workflowMetrics.signing)}
      />
      <MetricsCard
        linkHref={link.WorkflowList()}
        linkText="Open workflows"
        title="Signed Workflows"
        value={getValueString(workflowMetrics.signed)}
      />
      <MetricsCard
        linkHref={link.TemplateIndex()}
        linkText="Open templates"
        title="Templates"
        value={getValueString(workflowMetrics.templates)}
      />
      <MetricsCard
        linkHref={link.ReportIndex()}
        linkText="Open AI reports"
        title="AI Reports"
        value={getValueString(reportMetrics.aiReports)}
      />
      <MetricsCard
        linkHref={link.RepositoryDocuments()}
        linkText="Open documents"
        title="Documents"
        value={getValueString(documentMetrics.documents)}
      />
    </div>
  );
}

const linkContainerId = 'link-container';
const useMetricsCardStyles = makeStyles()(theme => ({
  link: {
    fontSize: 12,
  },
  linkContainer: {
    alignItems: 'center',
    color: theme.palette.primary.main,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    visibility: 'hidden',
  },
  metricTitle: {
    color: 'inherit',
    fontSize: 16,
  },
  metricValue: {
    color: 'inherit',
    fontSize: 48,
  },
  root: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    [`&:hover #${linkContainerId}`]: {
      color: theme.palette.primary.contrastText,
      visibility: 'visible',
    },
    borderRadius: 6,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    textDecoration: 'none',
  },
}));

function MetricsCard({
  linkHref,
  linkText,
  title,
  value,
}: {
  linkHref: string;
  linkText: string;
  title: string;
  value: string;
}) {
  const { classes } = useMetricsCardStyles();

  return (
    <Paper className={classes.root} component={Link} to={linkHref}>
      <Typography className={classes.metricTitle} component="h3" variant="subtitle1">
        {title}
      </Typography>
      <Spacer />
      <Typography className={classes.metricValue} variant="h1">
        {value}
      </Typography>
      <div className={classes.linkContainer} id={linkContainerId}>
        <Typography className={classes.link} color="inherit">
          {linkText}
        </Typography>
      </div>
    </Paper>
  );
}
